import React from 'react';
import styled from 'styled-components';
import { colors, text } from '@annote/core/styles';
import { useAIContext } from 'src/context/AIContextBackend';
import { Sparkles, FileText, Link2, Trash2 } from 'lucide-react';
import { parseSuggestionTitle, renderParsedTitle } from 'src/utils/parseSuggestionTitle';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
  background: ${colors.bg.primary};
  border-radius: 8px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${text.size.small};
  font-weight: ${text.weight.medium};
  color: ${colors.text.description};
  margin-bottom: 4px;
`;

const GroupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${colors.bg.surface};
  border: 1px solid ${colors.borders.input.primary};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  color: ${colors.text.primary};

  &:hover {
    background: ${colors.bg.hover};
    border-color: ${colors.borders.input.selected};
    box-shadow: ${colors.shadow.card.hover};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.text.description};
`;

const GroupTitle = styled.div`
  flex: 1;
  font-weight: ${text.weight.medium};
`;

const getIconForGroupType = (groupType: string): React.ReactNode => {
  switch (groupType) {
    case 'connect':
      return <Link2 size={16} />;
    case 'continue_reading':
      return <FileText size={16} />;
    case 'delete':
      return <Trash2 size={16} />;
    default:
      return <Sparkles size={16} />;
  }
};

const groupSuggestionsByCategory = (
  groups: Array<{ id: string; title: string; group_type: string }>,
): Record<string, Array<{ id: string; title: string; group_type: string }>> => {
  const categorized: Record<string, Array<{ id: string; title: string; group_type: string }>> = {
    'Connect together': [],
    'Continue reading': [],
    Organize: [],
    Delete: [],
  };

  groups.forEach((group) => {
    if (group.group_type === 'connect') {
      categorized['Connect together'].push(group);
    } else if (group.group_type === 'continue_reading') {
      categorized['Continue reading'].push(group);
    } else if (group.group_type === 'organize_notes') {
      categorized.Organize.push(group);
    } else if (group.group_type === 'delete' || group.group_type === 'cleanup') {
      categorized.Delete.push(group);
    }
  });

  return categorized;
};

export const SuggestionGroups: React.FC = () => {
  const { availableSuggestionGroups, applySuggestionGroup } = useAIContext();

  if (availableSuggestionGroups.length === 0) {
    return null;
  }

  const categorized = groupSuggestionsByCategory(availableSuggestionGroups);

  return (
    <Container data-testid="suggestion-groups-container">
      <SectionTitle>
        <Sparkles size={14} />
        Anni Suggestions
      </SectionTitle>

      {Object.entries(categorized).map(([category, groups]) => {
        if (groups.length === 0) return null;

        return (
          <GroupSection key={category}>
            <div style={{ fontSize: text.size.small, color: colors.text.placeholder, marginLeft: '4px' }}>
              {category}
            </div>
            {groups.map((group) => {
              const parsedTitle = parseSuggestionTitle(group.title);
              return (
                <GroupButton
                  key={group.id}
                  onClick={() => applySuggestionGroup(group.id)}
                  data-testid={`suggestion-group-${group.id}`}
                >
                  <IconWrapper>{getIconForGroupType(group.group_type)}</IconWrapper>
                  <GroupTitle>{renderParsedTitle(parsedTitle)}</GroupTitle>
                </GroupButton>
              );
            })}
          </GroupSection>
        );
      })}
    </Container>
  );
};
